// ─────────────────────────────────────────────────────────────────────────────
// MIRRORED FILE — must stay BYTE-IDENTICAL with its twin:
//   code/admin/actionable-frontend/src/components/forms/fieldTypes.ts
//   code/xoe-clinic/mygene.app/src/components/forms/fieldTypes.ts
// Enforced at PR time by tests/harness/scripts/form-field-type-guard.sh in the
// dev-tooling repo (runs under `make test-schema`).
//
// Keep this file dependency-free — no React, no antd, no repo-local imports —
// so the two copies can be diffed directly.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Choice field types are named for their MULTIPLICITY, not for the widget that
 * draws them:
 *
 *   single_select — radio buttons  — value is one option string
 *   multi_select  — checkboxes     — value is an array of option strings
 *   select        — dropdown       — value is one option string
 *   multiselect   — multi dropdown — value is an array of option strings
 *
 * The older `radio` / `checkbox` names described the widget and said nothing
 * about multiplicity, which is how the two came to be silently swapped in the
 * clinical renderer for a week in July 2026. They are read forever (see
 * LEGACY_FIELD_TYPE_ALIASES) but are never authored again.
 */
export type CanonicalFieldType =
  | 'text' | 'textarea' | 'email' | 'number' | 'phone'
  | 'date' | 'datetime' | 'time'
  | 'single_select' | 'multi_select' | 'select' | 'multiselect'
  | 'file' | 'signature'
  | 'heading' | 'paragraph';

/** Type strings still present in stored form structures. Never authored again. */
export type LegacyFieldType = 'radio' | 'checkbox';

/** Anything that may legitimately appear as a `type` in stored structure JSON. */
export type StoredFieldType = CanonicalFieldType | LegacyFieldType;

/**
 * Permanent read-only aliases. Stored `structure` JSON is never migrated, so
 * both sides of this table stay live indefinitely.
 */
export const LEGACY_FIELD_TYPE_ALIASES: Readonly<Record<LegacyFieldType, CanonicalFieldType>> = {
  radio: 'single_select',
  checkbox: 'multi_select',
};

/**
 * Resolve a stored type string to its canonical form. Unknown strings pass
 * through unchanged so callers still reach their own `default:` branch.
 */
export const normaliseFieldType = (type: string): string =>
  (LEGACY_FIELD_TYPE_ALIASES as Record<string, string>)[type] ?? type;

/**
 * Types that carry an `options` list. Used by the builder to seed defaults when a
 * field is switched INTO a choice type, so a converted field is usable on the spot.
 */
const CHOICE_CANONICAL: ReadonlySet<string> = new Set([
  'single_select', 'multi_select', 'select', 'multiselect',
]);

export const isChoiceFieldType = (type: string): boolean =>
  CHOICE_CANONICAL.has(normaliseFieldType(type));

/** Options seeded on a field that is dropped as, or converted into, a choice type. */
export const DEFAULT_CHOICE_OPTIONS: readonly string[] = ['Option 1', 'Option 2', 'Option 3'];

/**
 * The patch to apply when an author changes an existing field's type in the builder.
 *
 * Note what is NOT here: `label`, `question` and `required` are untouched, so the
 * caller merging this over the field preserves them. That is the entire point of
 * the feature — changing a mis-chosen type must not mean rebuilding the field.
 */
export const patchForTypeChange = (
  field: { options?: string[] },
  nextType: string,
): { type: string; options?: string[] } => {
  const patch: { type: string; options?: string[] } = { type: nextType };
  // Seed options only when entering a choice type that has none. Existing options
  // are left alone, so switching away and back does not lose the author's work.
  if (isChoiceFieldType(nextType) && !(field.options && field.options.length > 0)) {
    patch.options = [...DEFAULT_CHOICE_OPTIONS];
  }
  return patch;
};

/** Field types that always span the full grid width. Canonical keys only. */
const FULL_WIDTH_CANONICAL: ReadonlySet<string> = new Set([
  'heading', 'paragraph', 'textarea',
  'single_select', 'multi_select', 'multiselect',
  'signature', 'file',
]);

/**
 * Always normalise before the lookup — a legacy `radio` / `checkbox` field must
 * get the same full-width layout as its canonical twin. Reading the raw type
 * here would silently drop legacy fields back to one grid column.
 */
export const isFullWidthFieldType = (type: string): boolean =>
  FULL_WIDTH_CANONICAL.has(normaliseFieldType(type));

/**
 * single_select / select value: one option string. Tolerates an array, which is
 * the shape rows written between 24–31 Jul 2026 hold for these fields.
 */
export const toSingleValue = (value: unknown): unknown =>
  (Array.isArray(value) ? value[0] : value);

/**
 * multi_select / multiselect value: an array of option strings. Tolerates a
 * scalar, which is the shape rows written between 24–31 Jul 2026 hold.
 */
export const toMultiValue = (value: unknown): unknown[] => {
  if (Array.isArray(value)) return value;
  if (value === undefined || value === null || value === '') return [];
  return [value];
};

/**
 * Palette display names and raw type strings that past builder versions wrote into
 * `label`. Live templates hold `radio`, `Text (Single line)`, the space-eaten
 * `Text(Singleline)` and the icon-prefixed `¶  Long Text`, so matching is done on a
 * case-folded, non-alphanumeric-stripped key and every variant collapses together.
 * Covers both palettes: the clinic's formElementTypes and the admin's FORM_FIELD_TYPES.
 */
const WIDGET_NAME_SOURCES: readonly string[] = [
  'text', 'textarea', 'email', 'number', 'phone', 'date', 'datetime', 'time',
  'single_select', 'multi_select', 'select', 'multiselect', 'file', 'signature',
  'heading', 'paragraph', 'radio', 'checkbox',
  'Section Heading', 'Paragraph Text', 'Text (Single line)', 'Textarea (Multi-line)',
  'Number', 'Email', 'Phone Number', 'Single Select', 'Multi Select',
  'Dropdown (Single Select)', 'Dropdown (Multi Select)', 'Date Picker', 'Time Picker',
  'Date & Time Picker', 'File Upload', 'Signature',
  'Long Text', 'Date & Time', 'Dropdown', 'Heading', 'Paragraph',
];

const normaliseWidgetName = (value: string): string =>
  value.toLowerCase().replace(/[^a-z0-9]/g, '');

const WIDGET_NAMES: ReadonlySet<string> =
  new Set(WIDGET_NAME_SOURCES.map(normaliseWidgetName));

/**
 * True when `label` is the name of a widget rather than text an author wrote.
 * Blank counts as a widget name — there is nothing to recover from it.
 */
export const isWidgetName = (label?: string): boolean => {
  const normalised = normaliseWidgetName(label ?? '');
  return normalised === '' || WIDGET_NAMES.has(normalised);
};

/**
 * The text a field shows — to the author in the builder AND to the person filling
 * the form. One resolver, so those two can never disagree.
 *
 * `label` is a legacy builder nickname and is used only when `isWidgetName` says an
 * author wrote it. Some live templates keep their ONLY question text there, so
 * ignoring it blanks a real question; trusting it blindly captions a field with the
 * name of its own control.
 *
 * Structurally typed so the two repos' differing `FormElement` shapes both satisfy
 * it without this module importing either of them.
 */
export const fieldDisplayName = (
  field: { type: string; label?: string; question?: string; placeholder?: string },
): string => {
  const canonical = normaliseFieldType(field.type);
  const authored = isWidgetName(field.label) ? '' : (field.label ?? '').trim();
  // heading / paragraph hold their body text in `placeholder`, not `question`.
  if (canonical === 'heading' || canonical === 'paragraph') {
    return (field.placeholder ?? '').trim() || authored;
  }
  return (field.question ?? '').trim() || authored;
};
